import React, { Component } from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Switch, Typography, TextField, styled, Box, TextFieldProps, SwitchProps, colors, BoxProps } from '@mui/material'
import { IFormInputBase } from './types'
import { getErrorMessage } from './helpers'
import { dayjsCustom, mergeObjects, tryParseIntRequired } from '../utils'

const defaultFormatString = 'MMMM D, YYYY'

const dateExpiredClasses = {
  root: 'DateExpired-root',
  control: 'DateExpired-control',
  label: 'DateExpired-label',
  expired: 'DateExpired-expired',
  labelSwitch: 'DateExpired-labelSwitch',
  input: 'DateExpired-input',
  switch: 'DateExpired-switch'
}

interface ISlots<T> {
  rootProps?: BoxProps
  switchProps?: SwitchProps
  textFieldProps?: Partial<TextFieldProps>
}

interface IDateExpiredConfig<T> extends ISlots<T> {
  defaultValue?: any
  /** @default string */
  inputType?: 'number' | 'string'
  textFieldProps?: Partial<TextFieldProps>
  switchChecked?: boolean
  switchCheckedGetter?: (value: any, model?: Partial<T>) => boolean
}

interface IMergeConfig<T> extends IDateExpiredConfig<T> {
  switchChecked: boolean
  handleBlur: () => void
}

interface IProps<T> extends IFormInputBase<T>, IDateExpiredConfig<T> {
  slots?: ISlots<T>
}

interface IState {
  numberOfDays: number
  switchChecked: boolean
}

function CreateDateExpired<T>(param?: IDateExpiredConfig<T>): React.ComponentType<IProps<T>> {
  class DateExpired extends Component<IProps<T>, IState> {
    defaultNumberOfDays: number = 30
    private id
    private mergeConfigCached: IMergeConfig<T>
    constructor(props: IProps<T>) {
      super(props)
      this.mergeConfigCached = this.updateMergeConfig(props.slots) ?? {}
      this.state = {
        numberOfDays: this.getNumberOfDays(),
        switchChecked: this.mergeConfig.switchChecked
      }
      this.id = new Date().getTime().toString()
    }

    get mergeConfig(): IMergeConfig<T> {
      return this.mergeConfigCached
    }

    get defaultValueInput(): string | number {
      if (!this.state.switchChecked) return ''
      if (this.mergeConfig.inputType === 'number') {
        return this.state.numberOfDays
      } else {
        return this.getOffsetDate(this.state.numberOfDays, 'YYYY-MM-DDTHH:mm:ss.sssZ')
      }
    }

    componentDidUpdate(prevProps: IProps<T>) {
      if (prevProps.slots !== this.props.slots || prevProps.data !== this.props.data) {
        this.mergeConfigCached = this.updateMergeConfig(this.props.slots)
      }
    }

    //#region Render
    render() {
      const { slots } = this.props
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Wrap className={this.getRootClasses()} {...slots?.rootProps}>
            <input key={this.defaultValueInput} type='text' hidden name={this.props.name?.toString()} defaultValue={this.defaultValueInput} />
            <TextField {...this.mapTextFieldProps()} />
            <div className={dateExpiredClasses.control}>
              <Typography
                variant='caption'
                className={dateExpiredClasses.labelSwitch}
                {...{ component: 'label', htmlFor: this.id }}
                sx={{ color: this.state.switchChecked ? 'success.main' : '#767676' }}
              >
                {this.state.switchChecked ? 'Use Expiration Date' : 'No Expiration'}
              </Typography>
              <Switch
                id={this.id}
                size='small'
                color='success'
                checked={this.state.switchChecked}
                onChange={(_, checked) => this.handleSwitchChange(checked)}
                {...this.mergeConfig.switchProps}
              />
            </div>
          </Wrap>
        </LocalizationProvider>
      )
    }
    //#endregion

    mapTextFieldProps = (): TextFieldProps => {
      const { messageErrors, name, onBlur, slots } = this.props
      const disabled = this.props.disabled || !this.state.switchChecked
      const obj: TextFieldProps = {
        fullWidth: true,
        className: dateExpiredClasses.input,
        label: (
          <span className={dateExpiredClasses.label}>
            Expiry date
            {this.state.switchChecked && <b>{this.getOffsetDate(this.state.numberOfDays)}</b>}
          </span>
        ),
        variant: 'outlined',
        type: 'number',
        disabled: disabled,
        value: this.state.switchChecked ? this.state.numberOfDays : 0,
        onChange: this.handleChange,
        ...slots?.textFieldProps
      }
      if (!!name) {
        obj.onBlur = () => onBlur && onBlur(name)
        const temp = getErrorMessage(messageErrors, name)
        if (temp.error) {
          obj.error = Boolean(temp.error)
          obj.helperText = temp.message ?? ''
        }
      }
      return mergeObjects<TextFieldProps>({}, obj, this.mergeConfig.textFieldProps)
    }

    updateMergeConfig(currentSlots?: ISlots<T>): IMergeConfig<T> {
      const obj = mergeObjects(param, this.props, currentSlots)
      const { data, name, onBlur, defaultValue, switchChecked, switchCheckedGetter } = this.props
      const dValue = defaultValue ?? (!!data && !!name ? data[name]?.toString() : undefined) ?? param?.defaultValue?.toString()
      const effectiveSwitchChecked = switchChecked ?? param?.switchChecked
      let check = !data && dValue === undefined && effectiveSwitchChecked === undefined ? false : !!dValue || !!effectiveSwitchChecked
      if (switchCheckedGetter) check = switchCheckedGetter(dValue, data)
      return {
        ...obj,
        switchChecked: check,
        defaultValue: dValue,
        handleBlur: () => {
          if (!name || !onBlur) return
          onBlur(name)
        }
      }
    }

    handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const numberOfDays: number = e.target.value != '' ? parseInt(e.target.value) : 0
      this.setState({ numberOfDays }, () => {
        const err = getErrorMessage(this.props.messageErrors, this.props.name)
        if (numberOfDays > 0 && err.error) this.mergeConfig.handleBlur()
      })
    }

    handleSwitchChange = (checked: boolean) => {
      this.setState({ switchChecked: checked }, () => {
        const { name, onBlur } = this.props
        if (!name || !onBlur) return
        onBlur(name)
      })
    }

    getRootClasses = () => {
      const classes = [dateExpiredClasses.root]
      if (this.state.numberOfDays < 1) classes.push(dateExpiredClasses.expired)
      return classes.join(' ')
    }

    getNumberOfDays = (): number => {
      const dValue = this.mergeConfig.defaultValue
      if (this.mergeConfig.inputType === 'number' || (dValue !== undefined && !isNaN(Number(dValue)))) {
        return tryParseIntRequired(dValue, this.defaultNumberOfDays)
      } else {
        return this.getDaysUntilDate(dValue, this.defaultNumberOfDays)
      }
    }

    getOffsetDate = (num: number, formatString = defaultFormatString): string => {
      return dayjsCustom().add(num, 'day').format(formatString)
    }

    getDaysUntilDate = (value?: string | number, defaultValue = 0): number => {
      try {
        if (!value) return defaultValue
        const val = typeof value === 'number' ? dayjsCustom().add(value, 'day') : dayjsCustom(value)
        if (!val.isValid()) return defaultValue
        const target = dayjsCustom(value)
        const today = dayjsCustom()
        const diff = target.diff(today, 'day', true)
        return Math.round(diff)
      } catch {
        return defaultValue
      }
    }
  }
  return DateExpired
}
export default CreateDateExpired

const Wrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  position: 'relative',
  [`.${dateExpiredClasses.switch}`]: {
    margin: 0,
    flex: '0 0 auto'
  },
  [`.${dateExpiredClasses.label}`]: {
    b: {
      color: colors.blue[600],
      marginLeft: '8px'
    }
  },
  [`.${dateExpiredClasses.labelSwitch}`]: {
    fontWeight: 600,
    cursor: 'pointer'
  },
  [`.${dateExpiredClasses.control}`]: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  [`.${dateExpiredClasses.input}`]: {
    '.MuiInputBase-input': {
      paddingRight: '160px'
    }
  },
  [`&.${dateExpiredClasses.expired}`]: {
    [`.${dateExpiredClasses.label}`]: {
      b: {
        color: colors.red[600]
      }
    }
  }
})
